import {
  WrenchScrewdriverIcon,
  IdentificationIcon,
  ChatBubbleLeftRightIcon,
  CalendarDaysIcon,
  BanknotesIcon,
  ChartBarIcon,
  Cog6ToothIcon,
} from '@heroicons/vue/24/outline'

// Every permission a contact can hold, as the Directory's editor offers them.
export const PERMISSION_KEYS = [
  { key: 'admin',          label: 'Admin'          },
  { key: 'teacher',        label: 'Teacher'        },
  { key: 'lesson_signups', label: 'Lesson Signups' },
  { key: 'memberships',    label: 'Memberships'    },
  { key: 'rehearsals',     label: 'Rehearsals'     },
  { key: 'recitals',       label: 'Recitals'       },
  { key: 'financial',      label: 'Financial'      },
]

// Single source of truth for admin sidebar structure + visibility, shared by
// AdminNav.vue (the sidebar itself) and useSearchableViews.js (global search) —
// kept in one place so the two never drift on who can see what.
export const ALL_NAV = [
  {
    label: 'Schedule',
    icon: CalendarDaysIcon,
    permission: null,
    children: [
      // searchable: false — global search offers Calendar > Week/Month
      // instead (see useSearchableViews.js's CALENDAR_VIEWS), so this generic
      // sidebar link doesn't also show up as a fourth, redundant result.
      { to: '/admin/calendar/day', label: 'Calendar',      permission: null, searchable: false },
      { to: '/admin/calendar/list', label: 'Lessons',      permission: null },
      { to: '/admin/lesson-series', label: 'Lesson Series', permission: 'admin' },
      { to: '/admin/recital',       label: 'Recital',       permission: ['admin', 'recitals'], keywords: ['recital registration', 'program', 'performers'] },
    ],
  },
  {
    label: 'Contacts',
    icon: IdentificationIcon,
    permission: ['admin', 'lesson_signups'],
    children: [
      { to: '/admin/contacts/directory', label: 'Directory', permission: ['admin', 'lesson_signups'] },
      { to: '/admin/contacts/students',  label: 'Students',  permission: ['admin', 'lesson_signups'] },
      { to: '/admin/contacts/teachers',  label: 'Teachers',  permission: ['admin', 'lesson_signups'] },
      { to: '/admin/submissions',        label: 'Submissions', permission: 'lesson_signups', tabs: [
        { key: 'lessons',   label: 'Lesson Inquiries',   keywords: ['signups', 'leads'] },
        { key: 'rehearsal', label: 'Rehearsal Bookings', keywords: ['room'] },
        { key: 'forms',     label: 'Forms',              keywords: ['feedback', 'financial aid', 'rate request'] },
      ] },
    ],
  },
  {
    // One group for every channel: personal email (Email), two-way SMS
    // (Texts), one-to-many email/SMS (Broadcast), plus both delivery logs (Logs,
    // tabbed).
    label: 'Messages',
    icon: ChatBubbleLeftRightIcon,
    permission: ['admin', 'lesson_signups'],
    children: [
      { to: '/admin/messages/inbox',     label: 'Email',     permission: 'admin', bmaOnly: true, keywords: ['inbox'] },
      { to: '/admin/messages/texts',     label: 'Texts',     permission: ['admin', 'lesson_signups'] },
      { to: '/admin/messages/broadcast', label: 'Broadcast', permission: ['admin', 'lesson_signups'] },
      { to: '/admin/messages/logs',      label: 'Logs',      permission: ['admin', 'lesson_signups'], tabs: [
        { path: 'email', label: 'Email Log', keywords: ['sent emails', 'opens'] },
        { path: 'texts', label: 'Text Log',  keywords: ['sms log', 'sent texts'] },
      ] },
    ],
  },
  {
    label: 'Billing & Payroll',
    icon: BanknotesIcon,
    permission: ['admin', 'memberships'],
    children: [
      { to: '/admin/financial',   label: 'Overview',    permission: 'admin', keywords: ['needs attention', 'outstanding', 'balances'] },
      { to: '/admin/billing-runs', label: 'Billing Runs', permission: 'admin', keywords: ['next billing run', 'billing preview', 'unbilled', 'pending charges', 'billing history', 'past charges', 'declines'] },
      { to: '/admin/payments',    label: 'Payments',    permission: 'admin', keywords: ['charge', 'refund'], tabs: [
        { key: 'all',   label: 'Payments' },
        { key: 'cash',  label: 'Cash & Checks', keywords: ['dropbox'] },
        { key: 'cards', label: 'Saved Cards',   keywords: ['payment methods', 'expiring'] },
      ] },
      { to: '/admin/memberships', label: 'Memberships', permission: ['admin', 'memberships'] },
      { to: '/admin/pricing',     label: 'Pricing',     permission: 'admin', tabs: [
        { path: 'discounts',         label: 'Discounts',         keywords: ['coupons'] },
        { path: 'lesson-packages',   label: 'Lesson Packages',   keywords: ['bundles'] },
        { path: 'membership-offers', label: 'Membership Offers' },
        { path: 'hardship-offer',    label: 'Hardship Offer' },
      ] },
      { to: '/admin/payroll',     label: 'Payroll',     permission: 'admin', tabs: [
        { key: 'pay',           label: 'Payroll' },
        { key: 'cancellations', label: 'Teacher Cancellations', keywords: ['days off'] },
      ] },
    ],
  },
  {
    label: 'Reporting',
    icon: ChartBarIcon,
    permission: 'admin',
    children: [
      { to: '/admin/reporting', label: 'Customer Spending', permission: 'admin' },
      { to: '/admin/site-analytics', label: 'Site Analytics', permission: 'admin' },
      { to: '/admin/widget-funnel', label: 'Widget Funnel', permission: 'admin' },
      { to: '/admin/w2-reporting', label: 'W-2 Reporting', permission: 'admin' },
    ],
  },
  {
    label: 'Sandbox',
    icon: WrenchScrewdriverIcon,
    permission: 'admin',
    bmaOnly: true,
    children: [
      { to: '/admin/onboarding-specialist', label: 'Onboarding Specialist', permission: ['admin', 'onboarding_specialist'], tabs: [
        { key: 'report', label: 'Report' },
        { key: 'rates',  label: 'Action Fee Rates', permission: 'admin' },
      ] },
      { to: '/admin/banking',              label: 'Banking',              permission: 'admin' },
      { to: '/admin/operations',           label: 'Operations',           permission: null, tabs: [
        { key: 'recent-tasks',    label: 'Recent Tasks',    keywords: ['queue'] },
        { key: 'scheduled-jobs',  label: 'Scheduled Jobs',  keywords: ['scheduler', 'cron'] },
        { key: 'system-status',   label: 'System Status',   keywords: ['health'] },
        { key: 'script-triggers', label: 'Script Triggers' },
      ] },
      { to: '/admin/email-preview',        label: 'Email Preview',        permission: 'admin' },
      { to: '/admin/settings/dns-setup',   label: 'DNS & Email Setup',    permission: 'admin' },
      { to: '/admin/db-browser',           label: 'DB Browser',           permission: 'admin' },
    ],
  },
  {
    label: 'Settings',
    icon: Cog6ToothIcon,
    permission: 'admin',
    children: [
      { to: '/admin/settings/business-profile', label: 'Business Profile', permission: 'admin', tabs: [
        { path: 'profile',        label: 'General',        keywords: ['logo'] },
        { path: 'rooms',          label: 'Rooms',          keywords: ['studios'] },
        // BMA is the platform account itself and never connects its own
        // Stripe account here (see admin_stripe_connect.py).
        { path: 'stripe',         label: 'Payments',       keywords: ['stripe connect'], hideForBma: true },
        { path: 'site-analytics', label: 'Site Analytics', keywords: ['tracking'] },
        { path: 'agreement',      label: 'Agreement & Policies', keywords: ['late fee', 'member agreement', 'terms', 'contract', 'waiver'] },
      ] },
      { to: '/admin/settings/widgets', label: 'Widgets', permission: 'admin' },
      { to: '/admin/settings/notifications', label: 'Notifications', permission: 'admin' },
    ],
  },
]

// `tabs` on a child are that view's own tab bar, rendered by TabBar.vue via
// useRouteTab.js and offered by global search (useSearchableViews.js). Each
// tab is either query-mode (`key` → `?tab=key`, the view switches in place)
// or route-mode (`path` → a child route under the view's `to`). The first
// tab is the default — its link is the bare view path, never `?tab=`.
export function tabHref(view, tab) {
  if (tab.path) return `${view.to}/${tab.path}`
  return tab === view.tabs[0] ? view.to : `${view.to}?tab=${tab.key}`
}

export function findNavView(to) {
  for (const group of ALL_NAV) {
    const view = group.children?.find(c => c.to === to)
    if (view) return { group, view }
  }
  return null
}

// The nav view a route belongs to: the longest `to` that is the path or a
// parent of it (route-mode tabs like /admin/messages/logs/email sit under
// their view). null for routes the nav doesn't list (e.g. calendar month).
export function navViewFor(path) {
  let best = null
  for (const group of ALL_NAV) {
    for (const view of group.children || []) {
      const matches = path === view.to || path.startsWith(view.to + '/')
      if (matches && (!best || view.to.length > best.to.length)) best = view
    }
  }
  return best
}

// Where the sidebar's first link would take this user — NoAccess.vue's way out.
export function firstVisibleView(permissions, tenantId) {
  for (const group of ALL_NAV) {
    if (!canSee(group, permissions, tenantId)) continue
    const view = group.children?.find(c => canSee(c, permissions, tenantId))
    if (view) return view.to
  }
  return null
}

export function canSee(item, permissions, tenantId) {
  if (item.hideForBma && tenantId === 1) return false
  // "bmaOnly" items aren't a §10 Tenant.features toggle — they're permanently
  // CSML/BMA-only (tenant_id 1), e.g. Banking, Onboarding Specialist,
  // Operations. See utils/tenant_utils.py's require_bma_only() on the backend
  // side — this is UX only, the actual enforcement lives there.
  if (item.bmaOnly && tenantId !== 1) return false
  const permission = item.permission
  if (permission === null) return true
  if (permissions.includes('admin')) return true
  if (Array.isArray(permission)) return permission.some(p => permissions.includes(p))
  return permissions.includes(permission)
}
